import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { getSentSmsForGuest } from "@/lib/api/clients/SentSmsClient";
import { sentSmsIdOf } from "@/types/SentSms";
import { PanelEmptyState } from "./PanelEmptyState";
import { SentSmsEntry } from "./SentSmsEntry";

/**
 * The guest's SMS history (SMS channel wave) — the twin of `SentMailsList` on
 * `GET /api/v1/sms-sent/{eventId}/guest/{guestId}` (WORKFLOWS_READ). Mounted
 * only while the SMS channel is on and the role may read automations
 * (`SentMessagesPanel`).
 *
 * A failed read is NOT an empty history: it says so and offers a retry, the
 * lesson `SentMailsList` learned the hard way.
 */
export const SentSmsList: React.FC<{ eventId: string; guestId: string }> = ({ eventId, guestId }) => {
  const t = useTranslations("smsGuest");
  const tErrors = useTranslations("errors");
  const enabled = eventId !== "" && guestId !== "";
  const q = useQuery({
    queryKey: ["sentSms", eventId, guestId],
    queryFn: () => getSentSmsForGuest(eventId, guestId),
    enabled,
    retry: false,
  });

  if (!enabled) return null;
  if (q.isPending) return <LoadingSpinner />;
  if (q.isError) {
    return (
      <div className="p-4 text-center" role="alert" data-testid="sent-sms-error">
        <p className="font-error">{tErrors("somethingWentWrong")}</p>
        <button type="button" className="body-2-bold underline mt-2" onClick={() => q.refetch()}>
          {tErrors("retry")}
        </button>
      </div>
    );
  }
  const rows = q.data ?? [];
  if (rows.length === 0) return <PanelEmptyState riv="MailEmpty" title={t("empty")} />;
  return (
    <ul className="flex flex-col space-y-3 mt-3" data-testid="sent-sms-list">
      {rows.map((row, i) => (
        <SentSmsEntry key={sentSmsIdOf(row) || `${row.createdAt ?? ""}-${i}`} sms={row} />
      ))}
    </ul>
  );
};
